import * as glide from "./glide";

// Cloudflare Worker endpoint for resolving shortened Google Maps URLs
const RESOLVE_ENDPOINT = "https://map-url-resolver.ahmed-abied.workers.dev/resolve";

// In-memory cache for resolved URLs and extracted coordinates
const resolveCache = new Map<string, string>();
const resultCache = new Map<string, string>();

/**
 * Normalize a URL by stripping tracking params and cleaning whitespace
 */
function normalizeUrl(url: string): string {
    let clean = url.trim();
    // Remove common Google tracking params that don't affect the location
    clean = clean.replace(/[?&]g_st=[^&]*/g, "");
    clean = clean.replace(/[?&]force=pwa[^&]*/g, "");
    clean = clean.replace(/[?&]source=mlapk[^&]*/g, "");
    clean = clean.replace(/[?&]g_ep=[^&]*/g, "");
    // Fix leading ? becoming & after removal
    clean = clean.replace(/\?&/, "?");
    // Remove trailing ? or &
    clean = clean.replace(/[?&]$/, "");
    return clean;
}

/**
 * Extract lat/lng from a Google Maps URL string.
 * Supports multiple URL formats:
 *   - /@lat,lng          (place URLs)
 *   - ?q=lat,lng         (query param)
 *   - !3dlat!4dlng       (data params)
 *   - /search/.../@lat,lng
 *   - ll=lat,lng         (legacy)
 */
export function extractLatLng(url: string): string | null {
    if (!url || !url.trim()) return null;

    // Decode URL-encoded characters first
    let decoded: string;
    try {
        decoded = decodeURIComponent(url);
    } catch {
        decoded = url;
    }

    // Pattern 1: /@lat,lng (most common in place URLs)
    // e.g. /@23.5378532,58.3552513,15z/
    const atPattern = /@(-?\d+\.?\d*),(-?\d+\.?\d*)/;
    const atMatch = decoded.match(atPattern);
    if (atMatch) {
        const lat = parseFloat(atMatch[1]);
        const lng = parseFloat(atMatch[2]);
        if (isValidCoord(lat, lng)) {
            return formatCoord(lat, lng);
        }
    }

    // Pattern 2: ?q=lat,lng or ?q=lat%2Clng (query string)
    // e.g. ?q=23.5918045043945,58.3586235046387
    // Also handles: ?q=23.5943259%2C58.4465187
    const qPattern = /[?&]q=(-?\d+\.?\d*)[,+%2C](-?\d+\.?\d*)/i;
    const qMatch = decoded.match(qPattern);
    if (qMatch) {
        const lat = parseFloat(qMatch[1]);
        const lng = parseFloat(qMatch[2]);
        if (isValidCoord(lat, lng)) {
            return formatCoord(lat, lng);
        }
    }

    // Pattern 3: !3dlat!4dlng (data parameters in Google Maps URLs)
    // e.g. !3d23.6055547!4d58.4914551
    const dataPattern = /!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/;
    const dataMatch = decoded.match(dataPattern);
    if (dataMatch) {
        const lat = parseFloat(dataMatch[1]);
        const lng = parseFloat(dataMatch[2]);
        if (isValidCoord(lat, lng)) {
            return formatCoord(lat, lng);
        }
    }

    // Pattern 4: /ll=lat,lng (less common, legacy)
    const llPattern = /ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/;
    const llMatch = decoded.match(llPattern);
    if (llMatch) {
        const lat = parseFloat(llMatch[1]);
        const lng = parseFloat(llMatch[2]);
        if (isValidCoord(lat, lng)) {
            return formatCoord(lat, lng);
        }
    }

    return null;
}

/**
 * Format coordinates to a consistent precision (max 7 decimal places)
 * This avoids floating point noise while keeping enough precision (~1cm)
 */
function formatCoord(lat: number, lng: number): string {
    // Keep original precision up to 7 decimals (sub-meter accuracy)
    const latStr = parseFloat(lat.toFixed(7)).toString();
    const lngStr = parseFloat(lng.toFixed(7)).toString();
    return `${latStr},${lngStr}`;
}

/**
 * Validate that lat/lng are within valid ranges
 */
function isValidCoord(lat: number, lng: number): boolean {
    return (
        !isNaN(lat) &&
        !isNaN(lng) &&
        lat >= -90 &&
        lat <= 90 &&
        lng >= -180 &&
        lng <= 180
    );
}

/**
 * Check if a URL is a shortened Google Maps URL that needs resolving
 */
function isShortUrl(url: string): boolean {
    return (
        url.includes("maps.app.goo.gl") ||
        url.includes("goo.gl/maps") ||
        url.includes("g.co/kgs") ||
        url.includes("?cid=")
    );
}

/**
 * Check if a URL is an unsupported type (Knowledge Graph links)
 */
function isUnsupportedUrl(url: string): boolean {
    return false; // we now support g.co/kgs via html extraction in worker
}

/**
 * Check if input looks like a URL at all
 */
function looksLikeUrl(input: string): boolean {
    return input.startsWith("http://") || input.startsWith("https://");
}

/**
 * Resolve a shortened Google Maps URL via the Cloudflare Worker proxy
 */
async function resolveShortUrl(shortUrl: string): Promise<string | null> {
    // Check cache first
    if (resolveCache.has(shortUrl)) {
        return resolveCache.get(shortUrl)!;
    }

    try {
        const response = await fetch(RESOLVE_ENDPOINT, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ url: shortUrl }),
        });

        if (!response.ok) {
            console.error("URL resolution failed:", response.status);
            return null;
        }

        const data = await response.json();

        if (data.resolvedUrl) {
            resolveCache.set(shortUrl, data.resolvedUrl);
            return data.resolvedUrl;
        }

        return null;
    } catch (error) {
        console.error("URL resolution error:", error);
        return null;
    }
}

const mapPinIcon = `<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M24 4C16.268 4 10 10.268 10 18C10 28.5 24 44 24 44C24 44 38 28.5 38 18C38 10.268 31.732 4 24 4ZM24 23C21.239 23 19 20.761 19 18C19 15.239 21.239 13 24 13C26.761 13 29 15.239 29 18C29 20.761 26.761 23 24 23Z" fill="currentColor"/>
</svg>`;

export default glide.column({
    name: "Map Lat/Lng Extractor",
    description: "Extracts latitude and longitude from Google Maps URLs",
    author: "Ahmed Abied",
    about:
        "Pass a Google Maps URL and get back lat,lng coordinates. Supports shortened URLs (maps.app.goo.gl), full place URLs, and query-based URLs.",
    video: "",
    icon: mapPinIcon,

    params: {
        locationUrl: {
            displayName: "Location URL",
            type: "string",
        },
    },
    result: { type: "string" },

    async run(locationUrl) {
        if (locationUrl.value === undefined || !locationUrl.value.trim()) {
            return undefined;
        }

        const rawUrl = locationUrl.value.trim();

        // Check full result cache first (covers both direct + resolved)
        if (resultCache.has(rawUrl)) {
            return resultCache.get(rawUrl)!;
        }

        // Reject unsupported URL types
        if (isUnsupportedUrl(rawUrl)) {
            return undefined;
        }

        // If it doesn't look like a URL at all, skip silently
        if (!looksLikeUrl(rawUrl)) {
            return undefined;
        }

        // Normalize the URL (strip tracking params)
        const url = normalizeUrl(rawUrl);

        // First, try to extract directly from the URL
        const directResult = extractLatLng(url);
        if (directResult) {
            resultCache.set(rawUrl, directResult);
            return directResult;
        }

        // If it's a short URL, resolve it and try again
        if (isShortUrl(url)) {
            const resolvedUrl = await resolveShortUrl(url);
            if (resolvedUrl) {
                const resolvedResult = extractLatLng(resolvedUrl);
                if (resolvedResult) {
                    resultCache.set(rawUrl, resolvedResult);
                    return resolvedResult;
                }
            }
            // Short URL couldn't be resolved - return undefined (blank in Glide)
            return undefined;
        }

        // Couldn't extract - return undefined so Glide shows blank
        return undefined;
    },
});
